import React from 'react';
import { Button } from './Button';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

/**
 * Empty state component for list pages
 *
 * Displays when no items are found in the list.
 * Follows theme variables for perfect light/dark theme support.
 *
 * @example
 * <ListPageEmpty
 *   icon="inbox"
 *   message="Nenhum canal cadastrado"
 *   actionLabel="Criar primeiro canal"
 *   onAction={() => setIsModalOpen(true)}
 * />
 */
export interface ListPageEmptyProps {
  /** Icon to display */
  icon?: IconName;
  /** Main message to display */
  message: string;
  /** Secondary description (optional) */
  description?: string;
  /** Label for action button */
  actionLabel?: string;
  /** Callback when action button is clicked */
  onAction?: () => void;
  /** Button variant */
  buttonVariant?: 'primary' | 'secondary' | 'ghost';
  /** Label for secondary action button (optional) */
  secondaryActionLabel?: string;
  /** Callback when secondary action button is clicked */
  onSecondaryAction?: () => void;
  /** Secondary button variant */
  secondaryButtonVariant?: 'primary' | 'secondary' | 'ghost';
}

export const ListPageEmpty: React.FC<ListPageEmptyProps> = ({
  icon,
  message,
  description,
  actionLabel,
  onAction,
  buttonVariant = 'primary',
  secondaryActionLabel,
  onSecondaryAction,
  secondaryButtonVariant = 'secondary',
}) => {
  return (
    <div
      className="text-center py-16 rounded-lg"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        border: '1px solid var(--theme-card-border)',
      }}
    >
      {/* Icon */}
      {icon && (
        <div className="flex justify-center mb-4">
          <Icon
            name={icon}
            size={48}
            className="opacity-40"
            style={{ color: 'var(--theme-muted-text)' }}
          />
        </div>
      )}

      {/* Message */}
      <p
        className="mb-2 text-base font-medium"
        style={{ color: 'var(--theme-card-text)' }}
      >
        {message}
      </p>

      {/* Description */}
      {description && (
        <p
          className="mb-6 text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          {description}
        </p>
      )}

      {/* Action buttons */}
      {((actionLabel && onAction) || (secondaryActionLabel && onSecondaryAction)) && (
        <div className="flex flex-wrap items-center justify-center gap-3">
          {actionLabel && onAction && (
            <Button onClick={onAction} variant={buttonVariant}>
              {actionLabel}
            </Button>
          )}
          {secondaryActionLabel && onSecondaryAction && (
            <Button onClick={onSecondaryAction} variant={secondaryButtonVariant}>
              {secondaryActionLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

ListPageEmpty.displayName = 'ListPageEmpty';
